<script>
    import ApiClient from '../scripts/apiclient';
    import { keyScope } from '../scripts/keyboard';
    import EditItem from './EditItem.svelte';
    import PhotoItem from './PhotoItem.svelte';
    import PhotoGroupItem from './PhotoGroupItem.svelte';
    import PhotoEditItem from './PhotoEditItem.svelte';
    import PlaygroundItem from './PlaygroundItem.svelte';
    import DataUtils from '../scripts/DataUtils';
    import FormatHelper from '../scripts/FormatHelper';
    import PhotoGroupHelper from '../scripts/PhotoGroupHelper';
    import DraftPlaygroundItem from './DraftPlaygroundtem.svelte';

    let items = [];
    let loaded = false;

    export const sidebarInterface = {

        /**
         * Reloads the default sidebar content.
         */
        reload: function () {
            reload();
        },

        /**
         * Opens the next item in the sidebar, if there is one.
         */
        openNextItem: function () {
            openFirstItem();
        },

        /**
         * The current number of items in the sidebar.
         */
        count: 0,
    };

    $: sidebarInterface.count = items.length;

    $: reload();

    /**
     * Opens the topmost item of the sidebar, if there is one.
     *
     * Every item component publishes its open function on the item it presents, so that the
     * shortcut reaches whatever is first at the time it is pressed. Looking the item up here
     * means there is no registration to keep in sync as the list changes.
     */
    function openFirstItem() {
        let item = items[0];

        if (item && item.open) {
            item.open();
        }
    }

    function reload() {
        loadItems().then((itms) => {
            items = itms;
            loaded = true;
        });
    }

    // The shortcuts of the sidebar, available whenever no dialog is open
    keyScope({
        n: openFirstItem,
        u: reload,
    });

    /**
     * Opens an item when its row is clicked, so that the whole row is a target rather than
     * the link alone. Every item publishes its open function the same way it does for the
     * keyboard, so there is nothing here to keep in sync as the list changes.
     *
     * Clicks that landed on something of their own, the link itself or a copy button, are
     * left to it, and marking text ends in a click as well: opening a dialog on top of an
     * id that was just selected would be a surprise.
     *
     * @param {Object} item The item whose row was clicked.
     * @param {MouseEvent} event The click.
     */
    function openRow(item, event) {
        if (event.target.closest('a, button, input, select, textarea, label')) {
            return;
        }

        let selection = window.getSelection();
        if (selection && selection.toString().length > 0) {
            return;
        }

        if (item && item.open) {
            item.open();
        }
    }

    /**
     * Gets the country of the playground an item concerns. Playground items are the
     * playground themselves, everything else points at one.
     *
     * @param {Object} item A sidebar item.
     * @returns {String} A two letter country code, or null if the item has no country.
     */
    function getCountry(item) {
        if (item.country) {
            return item.country;
        }

        return item.playground ? item.playground.country : null;
    }

    /**
     * Removes an item from the list, e.g. when it has been processed.
     *
     * @param {Object} item The item to remove
     */
    function removeItem(item) {
        if (!item) {
            return;
        }

        let index = findItemIndex(item);
        if (index == -1) {
            return;
        }

        items.splice(index, 1);
        items = items; // Let Svelte know something has changed
    }

    /**
     * Find the index of the specified item in the list of items, using either the _id or tempId
     * properties for comparison.
     *
     * @param item The item to find.
     */
    function findItemIndex(item) {
        if (item._id) {
            return items.findIndex((i) => i._id == item._id);
        }

        if (item.tempId) {
            return items.findIndex((i) => i.tempId == item.tempId);
        }

        return -1;
    }

    /**
     * Handles a group of photos that has been through the approval popup. Photos that
     * were handled are dropped from the group, and the item is removed once the group
     * is empty.
     *
     * @param {Object} group The group to update.
     */
    function photoGroupHandled(group) {
        if (!group) {
            return;
        }

        group.photos = group.photos.filter((photo) => photo.state == 'draft');

        if (group.photos.length == 0) {
            removeItem(group);
            return;
        }

        items = items; // Let Svelte know something has changed
    }

    /**
     * Splits a group of photos into one item per photo, so that they can be handled one
     * by one. This is a UI operation only, nothing is stored, so the photos are grouped
     * again the next time the sidebar is loaded.
     *
     * @param {Object} group The group to split up.
     */
    function splitPhotoGroup(group) {
        if (!group || group.photos.length == 0) {
            return;
        }

        let index = findItemIndex(group);
        if (index == -1) {
            return;
        }

        // Continue right away with the first photo of the group
        group.photos[0].autoOpen = true;

        items.splice(index, 1, ...group.photos);
        items = PhotoGroupHelper.sortItems(items);
    }

    async function loadItems() {
        loaded = false;
        let items = await ApiClient.loadSidebarData();

        return PhotoGroupHelper.sortItems(PhotoGroupHelper.groupPhotoItems(items));
    }

    /** Cancel default behavior for file drag over events. */
    function dragOver(event) {
        return false;
    }

    /**
     * Handle file drop event, to open list of new playgrounds.
     *
     * @param event The file drop event.
     */
    async function fileDropped(event) {
        loaded = false;

        if (event.dataTransfer.items) {
            // Use DataTransferItemList interface to access the file(s)
            for (var i = 0; i < event.dataTransfer.items.length; i++) {
                // If dropped items aren't files, reject them
                if (event.dataTransfer.items[i].kind === 'file') {
                    let file = event.dataTransfer.items[i].getAsFile();
                    DataUtils.readJSON(file).then((json) => populateDraftPlaygrounds(json));
                }
            }
        } else {
            // Use DataTransfer interface to access the file(s)
            for (var i = 0; i < event.dataTransfer.files.length; i++) {
                let file = event.dataTransfer.files[i];
                DataUtils.readJSON(file).then((json) => populateDraftPlaygrounds(json));
            }
        }
    }

    function populateDraftPlaygrounds(data) {
        data.forEach((item) => {
            item.isDraftPlayground = true;
            item.tempId = Math.random().toString(16).substr(0, 8);
            item.state = 'draft';
        });

        items = data;
        loaded = true;
    }
</script>

<aside on:dragover|preventDefault={dragOver} on:drop|preventDefault={fileDropped}>
    {#if loaded}
        {#if items.length == 0}
            <p>Inkorgen är tom <i class="far fa-thumbs-up" /></p>
        {:else}
            <ul>
                {#each items as item}
                    <!-- The link inside stays the focusable control, so the row handler adds
                         a pointer target without taking anything away from the keyboard -->
                    <!-- svelte-ignore a11y-click-events-have-key-events -->
                    <li class:has-flag={getCountry(item)} on:click={(event) => openRow(item, event)}>
                        {#if getCountry(item)}
                            <span class="flag" title={getCountry(item).toUpperCase()}>
                                {FormatHelper.countryFlag(getCountry(item))}
                            </span>
                        {/if}
                        {#if item.isEdit}
                            <EditItem edit={item} editHandled={removeItem} />
                        {:else if item.isPlayground}
                            <PlaygroundItem playground={item} playgroundHandled={removeItem} />
                        {:else if item.isPhotoGroup}
                            <PhotoGroupItem group={item} groupHandled={photoGroupHandled} groupSplit={splitPhotoGroup} />
                        {:else if item.isPhoto}
                            <PhotoItem photo={item} photoHandled={removeItem} autoOpen={item.autoOpen} />
                        {:else if item.isPhotoEdit}
                            <PhotoEditItem photoEdit={item} photoEditHandled={removeItem} />
                        {:else if item.isDraftPlayground}
                            <DraftPlaygroundItem playground={item} playgroundHandled={removeItem} />
                        {/if}
                    </li>
                {/each}
            </ul>
        {/if}
    {:else}
        <div class="spinner-wrapper">
            <div class="spinner-border spinner-border-sm text-secondary" role="status">
                <span class="sr-only" />
            </div>
            Laddar ...
        </div>
    {/if}
</aside>

<style>
    p,
    .spinner-wrapper {
        margin: 10px;
    }

    aside {
        width: 100%;
        height: 100%;
        font-size: small;
        background: white;
    }

    ul {
        list-style: none;
        padding: 0;
        margin: 0;
    }

    li {
        position: relative;
        margin: 0;
        padding: 6px;
        cursor: pointer;
    }

    /* Keeps long titles from running in under the flag */
    li.has-flag {
        padding-right: 28px;
    }

    /* The item content is laid out without knowing about the flag, so it is placed on top
       in the corner rather than taking up space of its own */
    .flag {
        position: absolute;
        top: 6px;
        right: 6px;
        line-height: 1;
        pointer-events: none;
    }

    li:nth-child(even) {
        background: #eee;
    }

    /* Shows that the whole row is a target, not just the link. After the striping above,
       so that it wins on rows of either colour. */
    li:hover {
        background: #dce9f5;
    }
</style>
